<template>
  <div class="image-tasks-container">
    <div class="image-task-toolbar">
      <el-button type="primary" @click="openImageTaskDialog()">新增任务</el-button>
    </div>
    <div class="image-wall">
      <div
        v-for="task in imageTasks"
        :key="task.id"
        class="image-card"
      >
        <div class="ratio-box" @click="openImagePreview(task)">
          <video
            v-if="task.image_url && task.generate_type === 'video'"
            :src="resolveMediaUrl(task.image_url)"
            class="ratio-image"
            muted
            playsinline
          />
          <img
            v-else-if="task.image_url"
            :src="resolveMediaUrl(task.image_url)"
            class="ratio-image"
            alt=""
          />
          <div v-else class="pending-box">
            {{ task.generate_type === "video" ? "视频生成中" : "图片生成中" }}
          </div>
        </div>
        <div class="image-card-meta">
          <div class="task-id-row">任务 #{{ task.id }} {{ task.created_at }}</div>
          <div class="task-remark-row">
            <el-input
              v-model="task.remark"
              size="mini"
              placeholder="备注(关联信息)"
              @blur="handleUpdateTaskRemark(task)"
              @keyup.enter.native="$event.target.blur()"
            />
          </div>
        </div>
        <div class="image-card-status">
          <el-tag size="mini">{{ task.generate_type === "video" ? "视频" : "图片" }}</el-tag>
          <el-tag size="mini" type="info">{{ task.provider === 'seedance' ? 'Seedance' : task.provider === 'klingai' ? 'KlingAI' : 'ComfyUI' }}</el-tag>
          <el-tag v-if="task.status === 'succeeded'" size="mini" type="success">已完成</el-tag>
          <el-tag v-else-if="task.status === 'queued'" size="mini" type="warning">排队中（可取消）</el-tag>
          <el-tag v-else-if="task.status === 'running'" size="mini" type="info">生成中</el-tag>
          <el-tag v-else-if="task.status === 'failed'" size="mini" type="danger">
            <el-popover
              v-if="task.error_message"
              placement="top-start"
              title="失败原因"
              width="300"
              trigger="hover"
              :content="task.error_message"
            >
              <span slot="reference" style="cursor: help">
                失败: {{ truncateText(task.error_message, 25) }}
              </span>
            </el-popover>
            <span v-else>失败</span>
          </el-tag>
          <el-tag v-else-if="task.status === 'cancelled'" size="mini">已取消</el-tag>
          <el-tag v-else-if="task.image_url" size="mini" type="success">已完成</el-tag>
          <el-tag v-else-if="task.cancelable" size="mini" type="warning">可取消</el-tag>
          <el-tag v-else size="mini" type="info">不可取消</el-tag>
          <el-tag v-if="task.status === 'running'" size="mini" type="info">
            {{ `进度 ${normalizeTaskProgress(task.progress)}%` }}
          </el-tag>
        </div>
        <div class="image-card-actions">
          <el-button type="text" @click="openImagePreview(task)" :disabled="!task.image_url">
            查看
          </el-button>
          <el-button type="text" @click="downloadTaskResult(task)" :disabled="!task.image_url">
            下载
          </el-button>
          <el-button type="text" @click="openImageTaskDialog(task)">编辑并重新生成</el-button>
          <el-button
            v-if="task.status === 'succeeded' && task.related_id"
            type="text"
            :loading="usingTaskId === task.id"
            @click="handleUseTask(task)"
          >
            使用
          </el-button>
          <el-button v-if="task.cancelable" type="text" @click="handleCancelImageTask(task)">
            删除任务
          </el-button>
        </div>
      </div>
    </div>
    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :current-page="imageTaskPage"
        :page-size="imageTaskPageSize"
        :total="imageTaskTotal"
        @current-change="handleImageTaskPageChange"
      />
    </div>

    <!-- Task Dialog -->
    <el-dialog
      :title="imageTaskForm.source_task_id ? '编辑并重新生成' : '新增生成任务'"
      :visible.sync="imageTaskDialogVisible"
      width="820px"
    >
      <el-form label-width="90px">
        <el-form-item label="备注">
          <el-input v-model="imageTaskForm.remark" placeholder="任务备注，如：第x集第y段" />
        </el-form-item>
        <el-form-item label="后端服务">
          <el-select v-model="imageTaskForm.provider" style="width: 160px" @change="onProviderChange">
            <el-option
              v-for="(config, key) in PROVIDER_CONFIGS"
              :key="key"
              :label="config.label"
              :value="key"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="生成类型">
          <el-select v-model="imageTaskForm.generate_type" style="width: 160px" @change="ensureWorkflowForGenerateType">
            <el-option label="图片" value="image" />
            <el-option label="视频" value="video" />
          </el-select>
        </el-form-item>
        <el-form-item label="工作流">
          <el-select
            v-model="imageTaskForm.workflow_name"
            style="width: 220px"
            @change="onWorkflowNameChange"
          >
            <el-option
              v-for="item in filteredWorkflowOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <template v-if="!isCloudVideoProvider">
          <el-form-item label="画幅比例">
            <el-radio-group v-model="imageTaskForm.aspect_ratio" @change="applyAspectRatioDefaults">
              <el-radio-button label="16:9" />
              <el-radio-button label="9:16" />
              <el-radio-button label="custom">自定义</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="imageTaskForm.aspect_ratio !== 'custom'" label="分辨率">
            <el-input-number
              v-model="imageTaskForm.image_width"
              :min="16"
              :step="1"
              @change="handlePresetWidthChange"
            />
            <span class="size-sep">x</span>
            <el-input-number
              v-model="imageTaskForm.image_height"
              :min="16"
              :step="1"
              @change="handlePresetHeightChange"
            />
          </el-form-item>
          <el-form-item v-if="imageTaskForm.aspect_ratio === 'custom'" label="自定义尺寸">
            <el-input-number v-model="imageTaskForm.image_width" :min="16" :step="16" />
            <span class="size-sep">x</span>
            <el-input-number v-model="imageTaskForm.image_height" :min="16" :step="16" />
          </el-form-item>
        </template>
        <template v-if="isCloudVideoProvider">
          <el-form-item label="输出分辨率">
            <el-select v-model="imageTaskForm.output_resolution" style="width: 200px" placeholder="分辨率">
              <el-option
                v-for="r in currentWorkflowConfig.outputResolutions || []"
                :key="r"
                :label="r"
                :value="r"
              />
            </el-select>
            <span class="form-hint-inline">（方舟 video 参数；2.0 fast 无 1080p）</span>
          </el-form-item>
          <el-form-item label="画幅比例">
            <el-select v-model="imageTaskForm.output_ratio" style="width: 200px" placeholder="画幅比">
              <el-option
                v-for="r in currentWorkflowConfig.outputRatios || []"
                :key="r"
                :label="r"
                :value="r"
              />
            </el-select>
            <span class="form-hint-inline">（API ratio，非像素长宽）</span>
          </el-form-item>
        </template>
        <el-form-item label="文字描述">
          <el-input v-model="imageTaskForm.prompt" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item v-if="supportsReferenceImages(imageTaskForm.workflow_name)" :label="referenceImagesFormLabel">
          <div class="ref-grid">
            <div v-for="(url, idx) in imageTaskForm.reference_images" :key="`${url}-${idx}`" class="ref-item">
              <img :src="resolveMediaUrl(url)" alt="" @click="openImagePreviewByUrl(url)" />
              <el-button type="text" @click="removeReferenceImage(idx)">移除</el-button>
            </div>
            <div v-if="!imageTaskForm.reference_images.length" class="media-empty-text">暂无{{ referenceImagesFormLabel }}</div>
          </div>
          <div class="ref-actions">
            <el-button size="mini" @click="openAssetPickerDialog">从现有资产选择</el-button>
            <el-upload
              :show-file-list="false"
              :http-request="handleTempImageUpload"
              action="#"
              :disabled="tempImageUploading"
              accept="image/*"
              class="upload-inline"
            >
              <el-button size="mini" :loading="tempImageUploading">
                {{ tempImageUploading ? `上传中 ${tempImageUploadProgress}%` : `本地上传${referenceImagesFormLabel}` }}
              </el-button>
            </el-upload>
          </div>
        </el-form-item>
        <el-form-item v-if="currentWorkflowConfig.supportsVideoConfig" label="视频参数">
          <template v-if="isCloudVideoProvider">
            <span class="video-config-label">时长</span>
            <el-select v-model="imageTaskForm.video_seconds" style="width: 200px" placeholder="时长">
              <el-option
                v-if="currentWorkflowConfig.allowAutoDuration"
                label="自动（由模型在 4–15 秒内选）"
                :value="-1"
              />
              <el-option
                v-for="s in cloudDurationOptions"
                :key="s"
                :label="`${s} 秒`"
                :value="s"
              />
            </el-select>
            <span class="form-hint-inline">（文档：2.0 / 2.0 fast 为 4–15 或 -1）</span>
          </template>
          <template v-else>
            <span class="video-config-label">时长(秒)</span>
            <el-input-number v-model="imageTaskForm.video_seconds" :min="1" :step="1" />
            <template v-if="currentWorkflowConfig.showVideoFps !== false">
              <span class="video-config-label video-config-gap">帧率</span>
              <el-input-number v-model="imageTaskForm.video_fps" :min="1" :step="1" />
            </template>
          </template>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="imageTaskDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveImageTask">提交任务</el-button>
      </div>
    </el-dialog>

    <!-- Asset Picker Dialog -->
    <el-dialog title="选择资产图片" :visible.sync="assetPickerDialogVisible" width="900px">
      <el-form :inline="true" class="inline-form">
        <el-form-item label="类型">
          <el-select v-model="assetPickerSearch.assetType" style="width: 140px">
            <el-option label="全部" value="all" />
            <el-option label="场景" value="场景" />
            <el-option label="角色" value="角色" />
            <el-option label="道具" value="道具" />
          </el-select>
        </el-form-item>
        <el-form-item label="名字">
          <el-input
            v-model="assetPickerSearch.keyword"
            placeholder="按名字搜索"
            clearable
            style="width: 220px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleAssetPickerSearch">搜索</el-button>
        </el-form-item>
      </el-form>
      <el-table
        ref="assetPickerTable"
        :data="filteredAssetsWithImage"
        border
        @selection-change="handleAssetPickerSelectionChange"
      >
        <el-table-column type="selection" width="50" />
        <el-table-column prop="name" label="名字" width="180" />
        <el-table-column prop="asset_type" label="类型" width="100" />
        <el-table-column label="图片">
          <template slot-scope="scope">
            <el-image
              :src="resolveMediaUrl(scope.row.image_url)"
              style="width: 120px; height: 80px"
              fit="cover"
              @click.native="openImagePreviewByUrl(scope.row.image_url)"
            />
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button @click="assetPickerDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmPickAssets">确定</el-button>
      </div>
    </el-dialog>

    <!-- Previews -->
    <el-dialog :visible.sync="imagePreviewDialogVisible" width="960px" title="图片预览" append-to-body>
      <img v-if="imagePreviewUrl" :src="imagePreviewUrl" style="width: 100%; border-radius: 4px;" alt="" />
    </el-dialog>

    <el-dialog title="视频预览" :visible.sync="videoPreviewDialogVisible" width="760px" @closed="closeVideoPreview" append-to-body>
      <video v-if="videoPreviewDialogVisible && videoPreviewUrl" :src="videoPreviewUrl" controls autoplay class="video-preview-dialog" />
    </el-dialog>
  </div>
</template>

<script>
import {
  listImageTasks,
  createImageTask,
  cancelImageTask,
  useImageTask,
  updateImageTaskRemark,
  uploadTempImage,
  getAssets
} from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";
import {
  DEFAULT_WORKFLOW_NAME,
  DEFAULT_PROVIDER,
  PROVIDER_CONFIGS,
  WORKFLOW_CONFIGS,
  getWorkflowConfig
} from "@/config/workflows";

const defaultImageTaskForm = () => ({
  provider: DEFAULT_PROVIDER,
  generate_type: "image",
  workflow_name: DEFAULT_WORKFLOW_NAME,
  aspect_ratio: "16:9",
  image_width: 1280,
  image_height: 720,
  output_resolution: "720p",
  output_ratio: "16:9",
  video_seconds: 5,
  video_fps: 15,
  prompt: "",
  remark: "",
  related_id: null,
  reference_images: [],
  source_task_id: null
});

export default {
  name: "ImageTasks",
  data() {
    return {
      imageTasks: [],
      imageTaskPage: 1,
      imageTaskPageSize: 20,
      imageTaskTotal: 0,
      imageTaskDialogVisible: false,
      imageTaskForm: defaultImageTaskForm(),
      imageTaskTimer: null,
      usingTaskId: null,
      PROVIDER_CONFIGS,
      workflowOptions: Object.keys(WORKFLOW_CONFIGS).map((name) => ({
        label: WORKFLOW_CONFIGS[name].label || name,
        value: name,
        provider: WORKFLOW_CONFIGS[name].provider || "comfyui"
      })),
      tempImageUploading: false,
      tempImageUploadProgress: 0,
      assetPickerDialogVisible: false,
      assets: [],
      assetPickerSearch: {
        assetType: "all",
        keyword: ""
      },
      assetPickerAppliedSearch: {
        assetType: "all",
        keyword: ""
      },
      pickedAssets: [],
      imagePreviewDialogVisible: false,
      imagePreviewUrl: "",
      videoPreviewDialogVisible: false,
      videoPreviewUrl: ""
    };
  },
  computed: {
    isCloudVideoProvider() {
      return (
        ((this.imageTaskForm.provider || "") === "seedance" ||
         (this.imageTaskForm.provider || "") === "klingai") &&
        (this.imageTaskForm.generate_type || "") === "video"
      );
    },
    /** 云端视频生成时长选项：Seedance 4–15s，KlingAI 3–15s */
    cloudDurationOptions() {
      const provider = this.imageTaskForm.provider;
      const min = provider === "klingai" ? 3 : 4;
      const o = [];
      for (let s = min; s <= 15; s += 1) o.push(s);
      return o;
    },
    currentWorkflowConfig() {
      const name = this.imageTaskForm.workflow_name || DEFAULT_WORKFLOW_NAME;
      return getWorkflowConfig(name) || WORKFLOW_CONFIGS[DEFAULT_WORKFLOW_NAME] || {};
    },
    filteredWorkflowOptions() {
      const provider = this.imageTaskForm.provider || DEFAULT_PROVIDER;
      const generateType = this.imageTaskForm.generate_type || "image";
      return this.workflowOptions.filter((item) => {
        // 按 provider 过滤
        if (item.provider !== provider) return false;
        // 按 generate_type 过滤
        const config = WORKFLOW_CONFIGS[item.value] || {};
        const allowed = config.allowedGenerateTypes || ["image"];
        return allowed.includes(generateType);
      });
    },
    filteredAssetsWithImage() {
      const keyword = (this.assetPickerAppliedSearch.keyword || "").trim().toLowerCase();
      return this.assets
        .filter((item) => (item.image_url || "").trim())
        .filter((item) => {
          const matchType =
            this.assetPickerAppliedSearch.assetType === "all" ||
            item.asset_type === this.assetPickerAppliedSearch.assetType;
          const matchName = !keyword || (item.name || "").toLowerCase().includes(keyword);
          return matchType && matchName;
        });
    },
    referenceImagesFormLabel() {
      return this.currentWorkflowConfig.referenceImagesLabel || "参考图";
    }
  },
  created() {
    this.loadImageTasks();
    this.startImageTaskPolling();
    this.handleQueryParams();
  },
  beforeDestroy() {
    this.stopImageTaskPolling();
  },
  methods: {
    resolveMediaUrl,
    truncateText(text, maxLen) {
      if (!text) return '';
      return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
    },
    async loadImageTasks() {
      try {
        const { data } = await listImageTasks({
          page: this.imageTaskPage,
          page_size: this.imageTaskPageSize
        });
        this.imageTasks = data.items || [];
        this.imageTaskTotal = data.total || 0;
      } catch (e) {
        console.error(e);
      }
    },
    handleImageTaskPageChange(page) {
      this.imageTaskPage = page;
      this.loadImageTasks();
    },
    startImageTaskPolling() {
      this.imageTaskTimer = setInterval(() => {
        const pending = this.imageTasks.some(t => !t.image_url && t.status !== 'failed' && t.status !== 'cancelled');
        if (pending) this.loadImageTasks();
      }, 5000);
    },
    stopImageTaskPolling() {
      if (this.imageTaskTimer) clearInterval(this.imageTaskTimer);
    },
    handleQueryParams() {
      const { action, type, id, name, prompt, image_url, episode_index, plan_index, duration, reference_images } = this.$route.query;
      if (action === "generate") {
        this.imageTaskForm = defaultImageTaskForm();
        if (type === "asset") {
          this.imageTaskForm.generate_type = "image";
          this.imageTaskForm.remark = `同步资产：${name}`;
          this.imageTaskForm.prompt = prompt || "";
          this.imageTaskForm.reference_images = image_url ? [image_url] : [];
          this.imageTaskForm.related_id = `asset:${id}`;
        } else if (type === "plan") {
          this.imageTaskForm.provider = "seedance";
          this.imageTaskForm.generate_type = "video";
          this.imageTaskForm.workflow_name = "seedance-2-0-fast";
          this.imageTaskForm.remark = `第${episode_index}集第${plan_index}段`;
          this.imageTaskForm.prompt = prompt || "";
          try {
            this.imageTaskForm.reference_images = reference_images ? JSON.parse(reference_images) : [];
          } catch (e) {
            this.imageTaskForm.reference_images = [];
          }
          this.imageTaskForm.related_id = `plan:${id}`;
          if (duration) {
            // For Seedance, clamp or use -1 if it's outside range? 
            // Better yet, just pass it and let validation handle it or use it as is if it fits.
            const d = Number(duration);
            const provider = this.imageTaskForm.provider;
            if (provider === 'seedance') {
               if (d >= 4 && d <= 15) this.imageTaskForm.video_seconds = d;
               else this.imageTaskForm.video_seconds = 5;
            } else if (provider === 'klingai') {
               if (d >= 3 && d <= 15) this.imageTaskForm.video_seconds = d;
               else this.imageTaskForm.video_seconds = 5;
            } else {
               this.imageTaskForm.video_seconds = d;
            }
          }
        }
        this.ensureWorkflowForGenerateType();
        this.clampReferenceImagesForWorkflow();
        this.imageTaskDialogVisible = true;
        // Clean up query params after handling them
        this.$router.replace({ path: this.$route.path, query: {} });
      }
    },
    openImageTaskDialog(task) {
      if (task) {
        this.imageTaskForm = {
          ...defaultImageTaskForm(),
          provider: task.provider || DEFAULT_PROVIDER,
          workflow_name: task.workflow_name || DEFAULT_WORKFLOW_NAME,
          generate_type: task.generate_type || "image",
          image_width: Number(task.image_width) || 1280,
          image_height: Number(task.image_height) || 720,
          output_resolution: task.output_resolution || "720p",
          output_ratio: task.output_ratio || "16:9",
          video_seconds: task.video_seconds === -1 || task.video_seconds === "-1" ? -1 : Number(task.video_seconds) || 5,
          video_fps: Number(task.video_fps) || 24,
          aspect_ratio: this.inferAspectRatio(task.image_width, task.image_height),
          prompt: task.prompt || "",
          remark: task.remark || "",
          related_id: task.related_id || null,
          reference_images: Array.isArray(task.reference_images) ? [...task.reference_images] : [],
          source_task_id: task.id
        };
      } else {
        this.imageTaskForm = defaultImageTaskForm();
      }
      this.ensureWorkflowForGenerateType();
      this.clampReferenceImagesForWorkflow();
      this.imageTaskDialogVisible = true;
    },
    inferAspectRatio(width, height) {
      const w = Number(width);
      const h = Number(height);
      if (w >= 16 && h >= 16 && Math.abs(w * 9 - h * 16) <= 1) return "16:9";
      if (w >= 16 && h >= 16 && Math.abs(w * 16 - h * 9) <= 1) return "9:16";
      return "custom";
    },
    ensureWorkflowForGenerateType() {
      const current = this.imageTaskForm.workflow_name;
      const available = this.filteredWorkflowOptions;
      if (!available.some(i => i.value === current)) {
        this.imageTaskForm.workflow_name = available.length ? available[0].value : "";
        // 切换工作流后，应用该工作流的默认参数
        const config = getWorkflowConfig(this.imageTaskForm.workflow_name);
        if (config) {
          if (config.defaultVideoSeconds) {
            this.imageTaskForm.video_seconds = config.defaultVideoSeconds;
          }
          if (config.defaultVideoFps) {
            this.imageTaskForm.video_fps = config.defaultVideoFps;
          }
          if (config.useArkOutputControls) {
            this.imageTaskForm.output_resolution = config.defaultOutputResolution || "720p";
            this.imageTaskForm.output_ratio = config.defaultOutputRatio || "16:9";
          }
        }
      }
    },
    onProviderChange() {
      // provider 切换时，重置工作流到该 provider 的第一个可用选项
      this.ensureWorkflowForGenerateType();
      this.clampReferenceImagesForWorkflow();
    },
    onWorkflowNameChange() {
      if (
        this.imageTaskForm.workflow_name === "seedance-2-0-fast" &&
        this.imageTaskForm.output_resolution === "1080p"
      ) {
        this.imageTaskForm.output_resolution = "720p";
      }
      this.clampReferenceImagesForWorkflow();
    },
    applyAspectRatioDefaults() {
      if (this.imageTaskForm.aspect_ratio === "16:9") {
        this.imageTaskForm.image_width = 1280;
        this.imageTaskForm.image_height = 720;
      } else if (this.imageTaskForm.aspect_ratio === "9:16") {
        this.imageTaskForm.image_width = 720;
        this.imageTaskForm.image_height = 1280;
      }
    },
    handlePresetWidthChange(val) {
      const ratio = this.getRatio();
      if (ratio) this.imageTaskForm.image_height = Math.round(val / ratio[0] * ratio[1]);
    },
    handlePresetHeightChange(val) {
      const ratio = this.getRatio();
      if (ratio) this.imageTaskForm.image_width = Math.round(val / ratio[1] * ratio[0]);
    },
    getRatio() {
      if (this.imageTaskForm.aspect_ratio === "16:9") return [16, 9];
      if (this.imageTaskForm.aspect_ratio === "9:16") return [9, 16];
      return null;
    },
    supportsReferenceImages(name) {
      return !!getWorkflowConfig(name)?.supportsReferenceImages;
    },
    clampReferenceImagesForWorkflow() {
      const wcfg = getWorkflowConfig(this.imageTaskForm.workflow_name);
      const max = wcfg?.maxReferenceImages;
      if (max === undefined || !Array.isArray(this.imageTaskForm.reference_images)) return;
      if (this.imageTaskForm.reference_images.length <= max) return;
      const label = wcfg?.referenceImagesLabel || "参考图";
      this.imageTaskForm.reference_images = this.imageTaskForm.reference_images.slice(0, max);
      if (max === 1) {
        this.$message.warning(`当前工作流仅支持 1 张${label}，已保留第一张`);
      } else {
        this.$message.warning(`当前工作流最多 ${max} 张${label}，已保留前 ${max} 张`);
      }
    },
    removeReferenceImage(idx) {
      this.imageTaskForm.reference_images.splice(idx, 1);
    },
    async openAssetPickerDialog() {
      try {
        const { data } = await getAssets();
        this.assets = data;
        this.assetPickerSearch = { assetType: "all", keyword: "" };
        this.assetPickerAppliedSearch = { assetType: "all", keyword: "" };
        this.assetPickerDialogVisible = true;
      } catch (e) {
        this.$message.error("加载资产失败");
      }
    },
    handleAssetPickerSearch() {
      this.assetPickerAppliedSearch = { ...this.assetPickerSearch };
    },
    handleAssetPickerSelectionChange(rows) {
      this.pickedAssets = rows;
    },
    confirmPickAssets() {
      const urls = this.pickedAssets.map(i => i.image_url).filter(Boolean);
      const wcfg = getWorkflowConfig(this.imageTaskForm.workflow_name);
      const max = wcfg?.maxReferenceImages;
      const label = wcfg?.referenceImagesLabel || "参考图";

      if (max === 1) {
        if (!urls.length) {
          this.assetPickerDialogVisible = false;
          return;
        }
        if (urls.length > 1) {
          this.$message.warning(`当前工作流仅支持 1 张${label}，已保留第一张`);
        }
        this.imageTaskForm.reference_images = [urls[0]];
        this.assetPickerDialogVisible = false;
        return;
      }

      let next = [...new Set([...this.imageTaskForm.reference_images, ...urls])];
      if (max !== undefined && next.length > max) {
        this.$message.warning(`当前工作流仅支持 ${max} 张${label}，已保留前 ${max} 张`);
        next = next.slice(0, max);
      }
      this.imageTaskForm.reference_images = next;
      this.assetPickerDialogVisible = false;
    },
    async handleTempImageUpload(opt) {
      this.tempImageUploading = true;
      try {
        const processedFile = await this._compressImage(opt.file);
        const { data } = await uploadTempImage(processedFile, (p) => { this.tempImageUploadProgress = p; });
        if (data.url) {
          const max = getWorkflowConfig(this.imageTaskForm.workflow_name)?.maxReferenceImages;
          if (max === 1) this.imageTaskForm.reference_images = [data.url];
          else this.imageTaskForm.reference_images.push(data.url);
        }
        this.$message.success("上传成功");
      } catch (e) {
        this.$message.error("上传失败");
      } finally {
        this.tempImageUploading = false;
      }
    },
    /**
     * 压缩图片：最大宽高 720px，移除 alpha 通道，输出 JPEG。
     * @param {File} file
     * @returns {Promise<File>}
     */
    _compressImage(file) {
      return new Promise((resolve, reject) => {
        const MAX = 720;
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            const ratio = Math.min(MAX / width, MAX / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          // 白底填充，移除 alpha 通道
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) return reject(new Error("图片压缩失败"));
            const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
            resolve(new File([blob], name, { type: "image/jpeg" }));
          }, "image/jpeg", 0.9);
        };
        img.onerror = () => reject(new Error("图片加载失败"));
        img.src = URL.createObjectURL(file);
      });
    },
    async saveImageTask() {
      if (!this.imageTaskForm.prompt) return this.$message.warning("文字描述必填");
      const wfCfg = getWorkflowConfig(this.imageTaskForm.workflow_name) || {};
      if (wfCfg.supportsReferenceImages) {
        const refs = this.imageTaskForm.reference_images || [];
        const label = wfCfg.referenceImagesLabel || "参考图";
        const required = wfCfg.referenceImagesRequired !== false; // 默认 true（兼容旧配置）
        if (required && !refs.length) return this.$message.warning(`请上传或选择${label}`);
        const m = wfCfg.maxReferenceImages;
        if (m && refs.length > m) {
          this.imageTaskForm.reference_images = refs.slice(0, m);
        }
      }
      try {
        const body = { ...this.imageTaskForm };
        if (!this.isCloudVideoProvider) {
          delete body.output_resolution;
          delete body.output_ratio;
        }
        await createImageTask(body);
        this.imageTaskDialogVisible = false;
        this.loadImageTasks();
        this.$message.success("任务已创建");
      } catch (e) {
        this.$message.error("创建失败");
      }
    },
    async handleUseTask(task) {
      this.usingTaskId = task.id;
      try {
        await useImageTask(task.id);
        this.$message.success("已应用结果");
      } catch (e) {
        this.$message.error("应用失败");
      } finally {
        this.usingTaskId = null;
      }
    },
    async handleCancelImageTask(task) {
      try {
        await this.$confirm("确认删除任务吗？", "提示", { type: "warning" });
        await cancelImageTask(task.id);
        this.loadImageTasks();
      } catch (e) {
        if (e !== "cancel") this.$message.error("操作失败");
      }
    },
    async handleUpdateTaskRemark(task) {
      try {
        await updateImageTaskRemark(task.id, task.remark);
        this.$message.success("备注已更新");
      } catch (e) {
        this.$message.error("更新失败");
      }
    },
    openImagePreview(task) {
      if (task.generate_type === "video") return this.openVideoPreview(task.image_url);
      this.imagePreviewUrl = resolveMediaUrl(task.image_url);
      this.imagePreviewDialogVisible = true;
    },
    openImagePreviewByUrl(url) {
      this.imagePreviewUrl = resolveMediaUrl(url);
      this.imagePreviewDialogVisible = true;
    },
    openVideoPreview(url) {
      this.videoPreviewUrl = resolveMediaUrl(url);
      this.videoPreviewDialogVisible = true;
    },
    closeVideoPreview() {
      this.videoPreviewDialogVisible = false;
      this.videoPreviewUrl = "";
    },
    async downloadTaskResult(task) {
      const url = resolveMediaUrl(task.image_url);
      window.open(url, "_blank");
    },
    normalizeTaskProgress(p) {
      return Math.round(p * 100) / 100;
    }
  }
};
</script>

<style scoped>
.form-hint-inline {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
  vertical-align: middle;
}
.image-task-toolbar { margin-bottom: 20px; }
.image-wall { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
.image-card { border: 1px solid #ebeef5; border-radius: 8px; overflow: hidden; }
.ratio-box { width: 100%; padding-top: 56.25%; position: relative; background: #f5f7fa; cursor: pointer; }
.ratio-image { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }
.pending-box { position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #909399; }
.image-card-meta { padding: 10px; }
.task-id-row { font-size: 12px; color: #999; margin-bottom: 5px; }
.image-card-status { padding: 0 10px 5px; }
.image-card-actions { padding: 5px 10px 10px; border-top: 1px solid #f0f0f0; }
.ref-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 10px; }
.ref-item img { width: 100%; height: 80px; object-fit: cover; border-radius: 4px; border: 1px solid #eee; }
.size-sep { margin: 0 8px; color: #999; }
.video-config-label { margin-right: 8px; color: #606266; }
.video-config-gap { margin-left: 20px; }
.video-preview-dialog {
  width: 100%;
  max-height: 75vh;
  display: block;
  border-radius: 4px;
}
</style>
